import {
  createAgentEventMessage,
  shouldApplyStreamSnapshot,
  shouldReplyToSnapshotRequest,
} from './cross-tab-policy.js';

function createDefaultSourceId() {
  return globalThis.crypto?.randomUUID?.() || `tab-${Date.now()}-${Math.random()}`;
}

function createDefaultChannel() {
  return typeof BroadcastChannel === 'function' ? new BroadcastChannel('aichat-session-sync') : null;
}

/**
 * Owns the BroadcastChannel lifecycle for one AI Chat tab.
 * The caller keeps the active-stream state, so a remote snapshot never replaces
 * a locally streaming message.
 */
export function createCrossTabSessionSync({
  sessionStore,
  messageStore,
  sessionFacade,
  updateTokenCounter,
  getLocalStreamingSessionId,
  channel = createDefaultChannel(),
  sourceId = createDefaultSourceId(),
  logger = console,
}) {
  const isCurrentSession = (sessionId) => {
    const currentSession = sessionStore.getSessionState().currentSession;
    return currentSession && String(currentSession.id) === String(sessionId);
  };

  async function syncRemoteSession(sessionId) {
    if (!isCurrentSession(sessionId) || String(getLocalStreamingSessionId()) === String(sessionId)) return;
    try {
      const result = await sessionFacade.fetchSessionSnapshot(sessionId);
      if (!isCurrentSession(sessionId)) return;
      sessionFacade.applySessionContext(result.context);
      messageStore.reconcileServerMessages(result.messages || []);
      sessionStore.upsertSession(result.session);
      updateTokenCounter();
    } catch (error) {
      logger.warn('[aichat] cross-tab session sync failed:', error?.message || error);
    }
  }

  function publishAgentEvent(sessionId, event) {
    channel?.postMessage(createAgentEventMessage({ source: sourceId, sessionId, event }));
  }

  function requestStreamingSnapshot(sessionId) {
    channel?.postMessage({ source: sourceId, sessionId, type: 'stream_snapshot_request' });
  }

  function handleAgentEvent(sessionId, event) {
    if (event.event === 'session_ready') {
      syncRemoteSession(sessionId);
    } else if (event.event === 'agent_state' && event.assistant_message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.assistant_message_id });
      messageStore.setAgentStatus(event.assistant_message_id, {
        state: event.state,
        context: { round: event.round, assistant_message_id: event.assistant_message_id },
      });
    } else if (event.event === 'agent_timeline' && event.assistant_message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.assistant_message_id });
      messageStore.appendAgentTimelineStep(event.assistant_message_id, {
        round: event.round,
        kind: event.kind,
        title: event.title,
        summary: event.summary,
      });
    } else if (event.event === 'agent_memory_load' && event.assistant_message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.assistant_message_id });
      messageStore.setAgentMemoryLoad(event.assistant_message_id, event.was_compressed ? ['已加载压缩会话记忆'] : []);
    } else if (event.event === 'agent_skill_activate' && event.assistant_message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.assistant_message_id });
      messageStore.appendAgentSkill(event.assistant_message_id, {
        skillId: event.skill_code,
        name: event.skill_name || event.skill_code,
        version: event.skill_version || null,
        purpose: event.purpose || '',
        tools: Array.isArray(event.tools) ? event.tools : [],
        unavailableTools: Array.isArray(event.unavailable_tools) ? event.unavailable_tools : [],
        versionSource: event.version_source || 'stable',
      });
    } else if (event.event === 'agent_quality_check' && event.assistant_message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.assistant_message_id });
      messageStore.setAgentQuality(event.assistant_message_id, {
        schemaVersion: event.schema_version,
        status: event.status,
        score: event.score,
        coverage: event.coverage,
        skills: Array.isArray(event.skills) ? event.skills : [],
        repair: event.repair || null,
      });
    } else if (event.event === 'message' && event.message_id != null) {
      messageStore.upsertAssistantMessage({ messageId: event.message_id });
      messageStore.replaceAssistantContent(event.message_id, event.answer || '');
    } else if (event.event === 'message_end' && event.message_id != null) {
      messageStore.finalizeAssistant(event.message_id, { latencyMs: event.latency_ms, finishReason: event.finish_reason });
      syncRemoteSession(sessionId);
    } else if (event.event === 'done' || event.event === 'error') {
      syncRemoteSession(sessionId);
    } else if (event.event === 'stream_cancelled') {
      setTimeout(() => syncRemoteSession(sessionId), 250);
    }
  }

  const onMessage = ({ data }) => {
    if (!data || data.source === sourceId || !data.sessionId || !isCurrentSession(data.sessionId)) return;
    const localStreamingSessionId = getLocalStreamingSessionId();
    if (data.type === 'stream_snapshot_request') {
      if (shouldReplyToSnapshotRequest({ localStreamingSessionId, sessionId: data.sessionId })) {
        channel?.postMessage({
          source: sourceId,
          sessionId: data.sessionId,
          type: 'stream_snapshot',
          messages: messageStore.getMessageState().messages,
        });
      }
      return;
    }
    if (data.type === 'stream_snapshot') {
      if (shouldApplyStreamSnapshot({ localStreamingSessionId, sessionId: data.sessionId, messages: data.messages })) {
        messageStore.reconcileServerMessages(data.messages);
        updateTokenCounter();
      }
      return;
    }
    if (data.event) handleAgentEvent(data.sessionId, data.event);
  };

  channel?.addEventListener('message', onMessage);
  return {
    publishAgentEvent,
    requestStreamingSnapshot,
    syncRemoteSession,
    destroy() {
      channel?.removeEventListener?.('message', onMessage);
      channel?.close?.();
    },
  };
}
